/* global URL_MENU */
import React from 'react';
import logoPlick from '@assets/images/logo.svg';
import { useAuth } from '@context/AuthContext';
import { useDesktop } from '@utils/hooks/useDesktop';
import useModules from '@utils/hooks/useModules';
import { useNavigate } from 'react-router-dom';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { OrdersCounter } from './OrdersCounter';
import { openExternalUrl } from '@utils/navigationExternalUrl';
import { EXTERNAL_URLS } from '@utils/constants';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import {
  IconShare,
  IconTicket,
  IconFileText,
  IconBuildingStore,
  IconHelpCircle,
  IconExternalLink,
  IconLogout,
  IconPointFilled,
  IconClock,
  IconReceiptDollar,
  IconUsers,
  IconSettings,
  IconCloudCheck,
  IconBuildingBridge2,
  IconLinkPlus,
  IconMessage2Filled,
} from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';

type MenuItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  show: boolean;
  notIncludedOnCurrentPlan?: boolean;
  isRed?: boolean;
};

const HeaderV3 = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { commerce } = useGetCommerce();
  const { isFreemium, orderLimits } = useSubscription();
  const { hasModule } = useModules();
  const { trackEvent } = useTracking();
  const isDesktop = useDesktop();
  const [showFounderWarning, setShowFounderWarning] = React.useState(false);
  const [showFounderForm, setShowFounderForm] = React.useState(false);

  const getNavigatePath = (moduleCheck: boolean | null, path: string) => {
    return moduleCheck ? path : `suscripcion/${path}`;
  };

  const mainMenuItems: MenuItem[] = [
    {
      id: 'mi-cuenta',
      label: 'Mi cuenta Plick',
      icon: <IconBuildingStore />,
      onClick: () => {
        navigate('mi-cuenta');
        // Cerrar menú desmarcando el checkbox
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: true,
    },
    {
      id: 'perfil-social',
      label: 'Perfil Social',
      icon: <IconLinkPlus />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.LINK_PROFILE), 'perfil-social'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.LINK_PROFILE)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.LINK_PROFILE),
    },
    {
      id: 'compartir-menu',
      label: 'Compartir menú',
      icon: <IconShare />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.COMPARTIR), 'compartir-menu'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.COMPARTIR)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.COMPARTIR),
    },
    {
      id: 'datos-del-comercio',
      label: 'Datos del comercio',
      icon: <IconBuildingStore />,
      onClick: () => {
        navigate('datos-del-comercio');
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.DATOS_COMERCIO)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.DATOS_COMERCIO),
    },
    {
      id: 'horarios',
      label: 'Horarios',
      icon: <IconClock />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.HORARIOS), 'horarios'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.HORARIOS)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.HORARIOS),
    },
    {
      id: 'tarifas',
      label: 'Tarifas y entregas',
      icon: <IconReceiptDollar />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.TARIFAS), 'tarifas'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.TARIFAS)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.TARIFAS),
    },
    {
      id: 'mesas',
      label: 'Mesas',
      icon: <IconBuildingBridge2 />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.SELF_SERVICE), 'mesas'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.SELF_SERVICE)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.SELF_SERVICE),
    },
    {
      id: 'cupones',
      label: 'Cupones',
      icon: <IconTicket />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.CUPONES), 'cupones'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.CUPONES)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.CUPONES),
    },
    {
      id: 'usuarios',
      label: 'Alta de usuarios',
      icon: <IconUsers />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.USUARIOS), 'usuarios'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.USUARIOS)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.USUARIOS),
    },
    {
      id: 'crear-anuncio',
      label: 'Crear anuncio',
      icon: <IconFileText />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.ANUNCIOS), 'anuncios'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.ANUNCIOS)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.ANUNCIOS),
    },
  ];

  const otherMenuItems: MenuItem[] = [
    {
      id: 'ajustes',
      label: 'Ajustes',
      icon: <IconSettings />,
      onClick: () => {
        navigate(getNavigatePath(hasModule(ModuleName.AJUSTES), 'ajustes'));
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: isFreemium || Boolean(hasModule(ModuleName.AJUSTES)),
      notIncludedOnCurrentPlan: !hasModule(ModuleName.AJUSTES),
    },
    {
      id: 'ayudanos-a-mejorar',
      label: 'Ayúdanos a mejorar',
      icon: <IconMessage2Filled />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
        setShowFounderWarning(true);
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: true,
    },
    {
      id: 'soporte',
      label: 'Soporte en línea',
      icon: <IconHelpCircle />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.WhatsAppSupportClicked);
        openExternalUrl(EXTERNAL_URLS.SUPPORT_WHATSAPP);
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: true,
    },
    {
      id: 'ir-menu',
      label: 'Ir a mi menú',
      icon: <IconExternalLink />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.MenuViewClicked);
        window.open(`${URL_MENU}/${commerce.id_path}`);
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: true,
    },
    {
      id: 'integraciones',
      label: 'Integraciones',
      icon: <IconCloudCheck />,
      onClick: () => {
        navigate('integraciones');
        const checkbox = document.getElementById('burger-toggle') as HTMLInputElement;
        if (checkbox) checkbox.checked = false;
      },
      show: true,
    },
    {
      id: 'cerrar-sesion',
      label: 'Cerrar sesión',
      icon: <IconLogout />,
      onClick: () => {
        trackEvent(TRACK_EVENT_NAMES.LogoutClicked);
        auth.logout();
      },
      show: true,
      isRed: true,
    },
  ];

  return (
    <>
      {/* Checkbox oculto */}
      <input type="checkbox" id="burger-toggle" />

      {/* Header superior */}
      <div className="fixed w-full z-50 bg-color-primary flex justify-between text-color-white h-16 items-center p-4 lg:px-8">
        {/* Logo */}
        <div className="flex items-baseline">
          <img src={logoPlick} alt="logo plick" height="25px" />
        </div>

        <div className="flex gap-4">
          <div className="flex gap-6">
            {/* OrdersCounter */}
            {orderLimits?.currentMonthCount !== undefined &&
            orderLimits?.currentMonthCount !== null &&
            orderLimits?.monthlyLimit !== undefined &&
            orderLimits?.monthlyLimit !== null &&
            orderLimits?.quotaUsagePercent !== undefined &&
            orderLimits?.quotaUsagePercent !== null ? (
              <OrdersCounter
                isDesktop={isDesktop}
                current={orderLimits.currentMonthCount}
                limit={orderLimits.monthlyLimit}
                usagePercent={orderLimits.quotaUsagePercent < 100 ? orderLimits.quotaUsagePercent : 100}
              />
            ) : null}

            {/* Botón hamburguesa solo en móvil */}
            {!isDesktop && auth.isReady && (
              <label
                htmlFor="burger-toggle"
                className="header-v3-burger-container cursor-pointer p-2 hover:bg-white/10 rounded-md transition-colors"
              >
                <div className="header-v3-burger-line"></div>
                <div className="header-v3-burger-line"></div>
                <div className="header-v3-burger-line"></div>
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      <label htmlFor="burger-toggle" className="header-v3-nav-overlay"></label>

      {/* Menú desplegable */}
      <nav className="header-v3-nav-menu">
        <div className="px-2">
          <section className="px-2 py-4 flex flex-col">
            {/* Sección principal */}
            <div className="bg-color-white rounded-lg p-1 mb-4">
              {mainMenuItems
                .filter((item) => item.show)
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={item.onClick}
                    className="relative w-full flex items-center gap-3 p-4 text-left active:bg-color-gray_light"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="text-color-gray_ultra_dark">{item.icon}</div>
                    <span className="text-color-gray_ultra_dark font-medium">{item.label}</span>
                    {item.notIncludedOnCurrentPlan && (
                      <div className="absolute right-3">
                        <IconPointFilled className="text-color-secondary" size={12} />
                      </div>
                    )}
                  </button>
                ))}
            </div>

            {/* Separador */}
            <div className="px-4 py-3 text-center">
              <h3 className="text-sm font-medium text-color-white opacity-90">Otras opciones...</h3>
            </div>

            {/* Sección "Otras opciones" */}
            <div className="bg-color-white rounded-lg p-1">
              {otherMenuItems
                .filter((item) => item.show)
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={item.onClick}
                    className="w-full flex items-center gap-3 p-4 text-left active:bg-color-gray_light"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className={item.isRed ? 'text-color-warning' : 'text-color-gray_ultra_dark'}>{item.icon}</div>
                    <span className={`font-medium ${item.isRed ? 'text-color-warning' : 'text-color-gray_ultra_dark'}`}>
                      {item.label}
                    </span>
                  </button>
                ))}
            </div>
          </section>
        </div>
      </nav>

      {/* Modals de Feedback */}
      {showFounderWarning ? (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      ) : null}
      {showFounderForm ? <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} /> : null}
    </>
  );
};

export default HeaderV3;
